/* eslint-disable @typescript-eslint/no-restricted-imports */
/* eslint-disable prefer-custom-date-types/prefer-custom-date-types */
import { Directive, Host, Input, OnChanges, Self, SimpleChanges } from "@angular/core";
import { DateOrDateTime, DateTime, nameofFactory, UTCDate } from "@ankaadia/ankaadia-shared";
import { isArray, isDate, isNil } from "lodash";
import { Calendar } from "primeng/calendar";
// https://github.com/primefaces/primeng/issues/6841

/* Updating UX from Server:
  1. The Server send the date string in UTC. Example: "2023-08-04T00:00:00.000Z"
  2. The json date extension converts all dates in local dates: Example: Thu Aug 03 2023 21:00:00 GMT-0300 (Horário Padrão de Brasília). Hence the utc date and the local date   represent the same point in time but in different time zones
  3. In writeValue we convert the local date in a local date with the date component taken from UTC only.
Hence Thu Aug 03 2023 21:00:00 GMT-0300 (Horário Padrão de Brasília)  becomes Fri Aug 04 2023 00:00:00 GMT-0300 (Horário Padrão de Brasília)

Updating Model and Server from UX
 1. The UX sends Fri Aug 04 2023 00:00:00 GMT-0300 (Horário Padrão de Brasília)
 2. We Convert it to Thu Aug 03 2023 21:00:00 GMT-0300 (Horário Padrão de Brasília) before writing the value to the model
 3. When sending the model to the server the json date extension serializes the UTC date string "2023-08-04T00:00:00.000Z"


Hence the model always contains a local date/time which becomes the write UTC date when converting it to UTC*/

const nameOfCalendar = nameofFactory<Calendar>();
const nameOf = nameofFactory<UtcFixDirective>();

@Directive({ selector: "p-calendar" })
export class UtcFixDirective implements OnChanges {
  private readonly writeValueSource: Calendar["writeValue"];
  private readonly minDateDescriptor: PropertyDescriptor;
  private readonly maxDateDescriptor: PropertyDescriptor;
  private readonly timeZone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;

  private readonly registerOnChangeSource: Calendar["registerOnChange"];
  private onChangeSource: Parameters<Calendar["registerOnChange"]>[0];

  get isDateOnly(): boolean {
    return !this.datePicker.showTime && this.datePicker.dataType === "date";
  }

  @Input()
  showTime?: boolean;

  private originalValue: any;
  private originalMinValue: any;
  private originalMaxValue: any;

  constructor(@Host() @Self() private readonly datePicker: Calendar) {
    this.writeValueSource = this.datePicker.writeValue;
    this.datePicker.writeValue = (value): void => this.writeValue(value);

    this.registerOnChangeSource = this.datePicker.registerOnChange;
    this.datePicker.registerOnChange = (fn): void => this.registerOnChange(fn);

    const prototype = Object.getPrototypeOf(this.datePicker);
    this.minDateDescriptor = Object.getOwnPropertyDescriptor(prototype, nameOfCalendar("minDate"));
    this.maxDateDescriptor = Object.getOwnPropertyDescriptor(prototype, nameOfCalendar("maxDate"));

    Object.defineProperty(this.datePicker, nameOfCalendar("minDate"), {
      get: this.minDateDescriptor.get,
      set: (value: Date | DateOrDateTime): void => this.writeMinDate(value),
    });

    Object.defineProperty(this.datePicker, nameOfCalendar("maxDate"), {
      get: this.maxDateDescriptor.get,
      set: (value: Date | DateOrDateTime): void => this.writeMaxDate(value),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes[nameOf("showTime")] && !changes[nameOf("showTime")].firstChange) {
      this.writeValue(this.originalValue);
      this.writeMinDate(this.originalMinValue);
      this.writeMaxDate(this.originalMaxValue);
    }
  }

  private writeValue(value: any): void {
    this.originalValue = value;
    return this.writeValueSource.call(this.datePicker, this.convertFromDate(value));
  }

  private writeMinDate(value: Date | DateOrDateTime): void {
    this.originalMinValue = value;
    this.minDateDescriptor.set.call(this.datePicker, this.convertToDate(value));
  }

  private writeMaxDate(value: Date | DateOrDateTime): void {
    this.originalMaxValue = value;
    this.maxDateDescriptor.set.call(this.datePicker, this.convertToDate(value));
  }

  private registerOnChange(fn: typeof this.onChangeSource): void {
    this.onChangeSource = fn;
    fn = (value: any): void => this.onChange(value);
    return this.registerOnChangeSource.call(this.datePicker, fn);
  }

  private onChange(value: any): void {
    this.originalValue = value;
    return this.onChangeSource(this.convertFromDate(value));
  }

  private convertFromDate(value: Date): DateTime | UTCDate;
  private convertFromDate(value: Date[]): DateTime[] | UTCDate[];
  private convertFromDate(value: Date | Date[]): DateTime | UTCDate | DateTime[] | UTCDate[] {
    function convertToDateTime(date: Date, timeZone: string): DateTime {
      if (!isDate(date)) {
        return null;
      }

      date = new Date(date);
      date.setSeconds(0, 0);
      return DateTime.fromDate(date, timeZone);
    }

    function convertToUtcDate(date: Date): UTCDate {
      if (!isDate(date)) {
        return null;
      }

      return UTCDate.fromYMD(date.getFullYear(), date.getMonth(), date.getDate());
    }

    if (isArray(value)) {
      return this.isDateOnly
        ? value.map((date) => convertToUtcDate(date))
        : value.map((date) => convertToDateTime(date, this.timeZone));
    }

    return this.isDateOnly ? convertToUtcDate(value) : convertToDateTime(value, this.timeZone);
  }

  private convertToDate(value: Date | DateTime | UTCDate): Date;
  private convertToDate(value: (Date | DateTime | UTCDate)[]): Date[];
  private convertToDate(value: Date | DateTime | UTCDate | (Date | DateTime | UTCDate)[]): Date | Date[] {
    function convert(value: Date | DateTime | UTCDate): Date {
      if (isNil(value)) {
        return null;
      }

      if (value instanceof UTCDate) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
      }

      if (value instanceof DateTime) {
        const result = value.toDate();
        result.setSeconds(0, 0);
        return result;
      }

      if (isDate(value)) {
        const result = new Date(value);
        result.setSeconds(0, 0);
        return result;
      }

      throw new TypeError("Invalid type for date. Expected Date or UTCDate or DateTime.");
    }

    if (isArray(value)) {
      return value.map((v) => convert(v));
    }

    return convert(value);
  }
}
